// Sticky left-rail nav for the guide page. Highlights the current section
// using IntersectionObserver so the active link tracks scroll without us
// having to wire scroll listeners by hand. Hash navigation itself is left
// to the browser — `scroll-behavior: smooth` (in guide.scss) handles the
// animation and `scroll-margin-top` on .gr-prose h2/h3 leaves room for
// the sticky public navbar. (See packs/application.js for the Turbolinks
// opt-out that keeps same-path hash clicks from re-rendering the page.)

import React, { useEffect, useState } from 'react';

const GROUPS = [
  {
    title: 'Getting started',
    items: [
      { id: 'creating-a-team', label: 'Creating a team' },
      { id: 'app-home',        label: 'App Home' },
      { id: 'team-dashboard',  label: 'Team dashboard' },
    ],
  },
  {
    title: 'Estimating',
    items: [
      { id: 'estimating-issues', label: 'Estimating issues' },
      { id: 'voting',            label: 'Voting' },
      { id: 're-estimating',     label: 'Re-estimating' },
      { id: 'paused-polls',      label: 'Paused polls' },
    ],
  },
  {
    title: 'Backlog & Auto-polling',
    items: [
      { id: 'auto-polling',             label: 'Auto-polling' },
      { id: 'backlog-settings',         label: 'Backlog settings' },
      { id: 'turning-off-auto-polling', label: 'Turning it off' },
    ],
  },
  {
    title: 'Team settings',
    items: [
      { id: 'reminder-settings',     label: 'Reminders' },
      { id: 'averaging-settings',    label: 'Averaging' },
      { id: 'auto-polling-settings', label: 'Auto-polling' },
      { id: 'poll-display-settings', label: 'Poll display' },
      { id: 'jira-update-settings',  label: 'Jira updates' },
    ],
  },
  {
    title: 'Administration',
    items: [
      { id: 'team-ownership',  label: 'Team ownership' },
      { id: 'workspace-admin', label: 'Workspace admin' },
      { id: 'web-portal',      label: 'Web portal' },
      { id: 'managing-usage',  label: 'Managing usage' },
    ],
  },
  {
    title: 'Permissions',
    items: [
      { id: 'permissions-overview', label: 'Overview' },
      { id: 'slack-permissions',    label: 'Slack scopes' },
      { id: 'jira-permissions',     label: 'Jira scopes' },
    ],
  },
  {
    title: 'Pricing & billing',
    items: [
      { id: 'subscription',   label: 'Subscription' },
      { id: 'pricing-tiers',  label: 'Pricing tiers' },
      { id: 'seat-counting',  label: 'Seat counting' },
    ],
  },
  {
    title: 'Support',
    items: [
      { id: 'feedback', label: 'Feedback' },
    ],
  },
];

export default function GuideSidebar() {
  const [active, setActive] = useState(GROUPS[0].items[0].id);

  useEffect(() => {
    const ids = GROUPS.flatMap(g => g.items.map(i => i.id));
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '-96px 0px -55% 0px', threshold: 0 }
    );
    ids.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <nav className="guide-sidebar" aria-label="Guide sections">
      {GROUPS.map(g => (
        <div key={g.title} className="guide-sidebar-group">
          <h4>{g.title}</h4>
          {g.items.map(it => (
            <a
              key={it.id}
              href={`#${it.id}`}
              className={active === it.id ? 'is-active' : ''}
            >
              {it.label}
            </a>
          ))}
        </div>
      ))}
    </nav>
  );
}
